import {
  AppError,
  CommonErrorsKey,
  RPC_URLS,
  type SupportedChainId,
  chainIdToChain,
  contractBook,
  isSupportedChainId,
} from "@eulerxyz/core-sdk"
import { PriceServiceConnection } from "@pythnetwork/price-service-client"
import {
  http,
  type Address,
  type ContractFunctionName,
  PublicClient,
  createPublicClient,
  decodeAbiParameters,
  encodeFunctionData,
  getAbiItem,
  getAddress,
  sha256,
  zeroAddress,
} from "viem"
import type { Hex } from "viem"
import type { ContractFunctionResult, EVCCalls } from "../evc"
import { checkPythHex, nonNullable, radixSort } from "../utils"
import {
  deepCheckIfIsRedstoneClasicOracle,
  isCrossAdapter,
  isCrossAdapterLazyCheck,
  isERC4626Oracle,
  isEulerRouter,
  isEulerRouterLazyCheck,
  isPendleOracleLazyCheck,
  isPythOracle,
  isRedstoneCoreOracle,
  isUniswapAdapterLazyCheck,
} from "./guards"
import {
  type BaseOracleNamesAndQuotes,
  OracleDecoded,
  type OracleDetailedInfo,
  type OracleType,
  type OracleTypes,
  type PushOracles,
  oracleNameToFunction,
} from "./types"

const CACHE_TIME = 15_000

export const NO_BASE_ADDRESS = getAddress(
  "0x0000000000000000000000000000000000042069",
)
export const NO_QUOTE_ADDRESS = getAddress(
  "0x0000000000000000000000000000000000042070",
)

export class OracleBatchItemFactory {
  #chainId: SupportedChainId
  #pythHermesEndpoint: string
  #pythCache = new Map<Hex, { data: Hex[]; time: number }>()
  #failedPythAdapters = new Set<Hex>()
  oracleInfos = new Map<Address, OracleDetailedInfo>()
  #publicClient: PublicClient

  /**
   * @constructor
   * @param chainId - The chain ID.
   * @param {string} [pythHermesEndpoint="https://hermes.pyth.network"] - The endpoint for Pyth Hermes.
   * @param rpcUrls {Record<SupportedChainId, string>} - RPC URLS.
   */
  constructor(
    chainId: number,
    pythHermesEndpoint = "https://hermes.pyth.network",
    rpcUrl?: string,
  ) {
    if (!isSupportedChainId(chainId))
      throw new AppError(
        CommonErrorsKey.ChainNotSupported,
        "Unsupported chain ID",
      )
    this.#chainId = chainId
    this.#pythHermesEndpoint = pythHermesEndpoint
    /// added here for testing cron on aws
    this.#publicClient = createPublicClient({
      chain: chainIdToChain[chainId],
      transport: http(rpcUrl || RPC_URLS[chainId]),
    })
  }

  /**
   * Create batch items from the provided oracle information.
   * @param {OracleDetailedInfo[]} _oracleInfos - The oracle information to process.
   * @param {Address} account - The account to use for the batch item onBehalfOfAccount.
   * @param {boolean} [disableCache=false] - Whether to disable the cache.
   * @returns {Promise<EVCCalls>} - A promise that resolves to the prepared batch items.
   */
  createBatchItems(
    _oracleInfos: OracleDetailedInfo[],
    account: Address,
    disableCache = false,
  ): Promise<EVCCalls> {
    const oracleInfos = this.#getOracleInfos(_oracleInfos, disableCache)
    return this.#createBatchItems(oracleInfos, account, disableCache)
  }

  /**
   * Get the Pyth and Redstone adapters from the oracle information.
   * @param {OracleDetailedInfo[]} _oracleInfos - The oracle information to process.
   * @param {boolean} [disableCache=false] - Whether to disable the cache.
   * @returns {PushOracles} - The Pyth and Redstone adapters.
   */
  getPushOracles(
    _oracleInfos: OracleDetailedInfo[],
    disableCache = false,
  ): PushOracles {
    const oracleInfos = this.#getOracleInfos(_oracleInfos, disableCache)
    return this.#getPushAdapters(oracleInfos)
  }

  /**
   * Create batch items from the provided oracle information.
   * @param {PushOracles} pushOracles - The oracle information to process.
   * @param {Address} account - The account to use for the batch item onBehalfOfAccount.
   * @param {boolean} [disableCache=false] - Whether to disable the cache.
   * @returns {Promise<EVCCalls>} - A promise that resolves to the prepared batch items.
   */
  createBatchItemsFromPushOracles(
    pushOracles: PushOracles,
    account: Address,
    disableCache = false,
  ): Promise<EVCCalls> {
    return this.#getPythBatchItem(pushOracles.pyth, account, disableCache)
  }

  /**
   * Get the Pyth oracle contract address for the current chain.
   * @private
   * @returns {Address} - The Pyth oracle contract address.
   */
  get #getPythAddress(): Address {
    return contractBook.pythOracle.address[this.#chainId]
  }

  /**
   * Create batch items from the provided oracle information.
   * @private
   * @param {OracleTypes[]} _oracleInfos - The oracle information to process.
   * @param {Address} account - The account to use for the batch item onBehalfOfAccount.
   * @param {boolean} [disableCache=false] - Whether to disable the cache.
   * @returns {Promise<EVCCalls>} - A promise that resolves to the prepared batch items.
   */
  async #createBatchItems(
    _oracleInfos: OracleTypes[],
    account: Address,
    disableCache = false,
  ): Promise<EVCCalls> {
    const { pyth } = this.#getPushAdapters(_oracleInfos)

    return this.#getPythBatchItem(pyth, account, disableCache)
  }

  /**
   * Get the Pyth batch item.
   * @private
   * @param {PushOracles["pyth"]} adapters - The Pyth oracle adapters.
   * @param {Address} account - The account to use for the batch item onBehalfOfAccount.
   * @param {boolean} [disableCache=false] - Whether to disable the cache.
   * @returns {Promise<EVCCalls>} - A promise that resolves to the Pyth batch item.
   */
  async #getPythBatchItem(
    adapters: PushOracles["pyth"],
    account: Address,
    disableCache = false,
  ): Promise<EVCCalls> {
    if (!adapters.length) return []

    try {
      const updateData = await this.#getCachedPythVaas(adapters, disableCache)

      const data = encodeFunctionData({
        abi: contractBook.pythOracle.abi,
        functionName: "updatePriceFeeds",
        args: [updateData],
      })

      const pythContractAddress = this.#getPythAddress
      if (pythContractAddress === zeroAddress) {
        console.error(
          `[Error] Pyth contract address is zero for chainId: ${this.#chainId}`,
        )
      }
      if (pythContractAddress === zeroAddress) {
        // No pyth contract address for this chain
        return []
      }
      const fee = await this.#publicClient.readContract({
        address: pythContractAddress,
        abi: contractBook.pythOracle.abi,
        functionName: "getUpdateFee",
        args: [updateData],
      })

      return [
        {
          targetContract: pythContractAddress,
          onBehalfOfAccount: account,
          value: fee,
          data,
        },
      ]
    } catch (e) {
      console.log(e)
      return []
    }
  }

  /**
   * Get the cached Pyth VAAs.
   * @private
   * @param {PushOracles["pyth"]} adapters - The Pyth oracle adapters.
   * @param {boolean} [disableCache=false] - Whether to disable the cache.
   * @returns {Promise<Hex[]>} - A promise that resolves latest vaas for adapters.
   */
  async #getCachedPythVaas(
    adapters: PushOracles["pyth"],
    disableCache = false,
    retries = 1,
  ): Promise<Hex[]> {
    const key = sha256(
      new Uint8Array(
        radixSort(
          adapters
            .filter(
              (v) =>
                checkPythHex(v.feedId) &&
                !this.#failedPythAdapters.has(v.feedId),
            )
            .map((adapter) => parseInt(adapter.oracle, 16)),
        ),
      ),
    )

    const cachedData = this.#pythCache.get(key)
    const currentTime = Date.now()

    if (
      !disableCache &&
      cachedData &&
      currentTime - cachedData.time < CACHE_TIME
    ) {
      return cachedData.data
    }

    try {
      const priceService = new PriceServiceConnection(
        this.#pythHermesEndpoint,
        {
          httpRetries: retries,
          timeout: 100000,
          priceFeedRequestConfig: {
            binary: true,
          },
        },
      )

      // Get unique feed IDs that are valid and haven't failed
      const uniqueFeedIds = adapters
        .map((adapter) => adapter.feedId)
        .filter((v) => checkPythHex(v) && !this.#failedPythAdapters.has(v))
        .filter((v, i, arr) => arr.indexOf(v) === i)

      // Split feed IDs into batches of 20
      const batchSize = 20
      const batches = []
      for (let i = 0; i < uniqueFeedIds.length; i += batchSize) {
        batches.push(uniqueFeedIds.slice(i, i + batchSize))
      }

      // Fetch VAAs for each batch in parallel and convert to hex
      const batchRequests = batches.map(async (feedIdBatch) => {
        const vaas = await priceService.getLatestVaas(feedIdBatch)
        return vaas.map(
          (vaa) => `0x${Buffer.from(vaa, "base64").toString("hex")}` as const,
        )
      })

      // Merge all batch results
      const updateData = (await Promise.all(batchRequests)).flat()

      this.#pythCache.set(key, { data: updateData, time: currentTime })

      return updateData
    } catch (e: any) {
      if ("response" in e) {
        const hexPattern = /0x[a-fA-F0-9]{64}/g
        const hexStrings = e.response.data.match(hexPattern) as Hex[]
        if (hexStrings.length) {
          this.#failedPythAdapters = new Set([
            ...Array.from(this.#failedPythAdapters),
            ...hexStrings,
          ])
          return this.#getCachedPythVaas(adapters, disableCache, 6)
        }
      }
      throw e
    }
  }

  /**
   * Get the Pyth and Redstone adapters from the oracle information.
   * @private
   * @param {OracleTypes[]} oracleInfos - The oracle information.
   * @returns {PushOracles} - The Pyth and Redstone adapters.
   */
  #getPushAdapters(oracleInfos: OracleTypes[]): PushOracles {
    const pythOracles: PushOracles["pyth"] = []
    const redstoneOracles: PushOracles["redstone"] = []

    for (const { oracle, vault, ...oracleInfo } of oracleInfos) {
      if (isPythOracle(oracleInfo)) {
        pythOracles.push({ ...oracleInfo, oracle })
        continue
      }

      if (isRedstoneCoreOracle(oracleInfo)) {
        redstoneOracles.push({ ...oracleInfo, oracle })
        continue
      }

      if (isCrossAdapter(oracleInfo)) {
        const baseCross = this.#getOracleInfo({
          ...oracleInfo.oracleBaseCrossInfo,
          vault,
        })
        const crossQuote = this.#getOracleInfo({
          ...oracleInfo.oracleCrossQuoteInfo,
          vault,
        })
        const { pyth, redstone } = this.#getPushAdapters(
          [baseCross, crossQuote].filter(nonNullable),
        )
        for (const adapter of pyth) {
          pythOracles.push(adapter)
        }
        for (const adapter of redstone) {
          redstoneOracles.push(adapter)
        }
      }

      if (isEulerRouter(oracleInfo)) {
        const oracleInfos: OracleDetailedInfo[] = []
        if (oracleInfo.fallbackOracleInfo.oracle !== zeroAddress)
          oracleInfos.push({
            ...oracleInfo.fallbackOracleInfo,
            vault,
          })
        for (const resolvedOracle of oracleInfo.resolvedOraclesInfo) {
          if (resolvedOracle.oracle !== zeroAddress)
            oracleInfos.push({ ...resolvedOracle, vault })
        }

        const { pyth, redstone } = this.#getPushAdapters(
          oracleInfos.map(this.#getOracleInfo.bind(this)).filter(nonNullable),
        )
        for (const adapter of pyth) {
          pythOracles.push(adapter)
        }
        for (const adapter of redstone) {
          redstoneOracles.push(adapter)
        }
      }
    }

    return {
      pyth: pythOracles,
      redstone: redstoneOracles,
    }
  }

  /**
   * Get the oracle information.
   * @private
   * @param {OracleDetailedInfo[]} oracleInfos - The detailed oracle information.
   * @param {boolean} [disableCache=false] - Whether to disable the cache.
   * @returns {OracleTypes[]} - The oracle information.
   */
  #getOracleInfos(
    oracleInfos: OracleDetailedInfo[],
    disableCache = false,
  ): OracleTypes[] {
    const cachedOracleInfos = disableCache
      ? []
      : Array.from(this.oracleInfos.values())

    return [...cachedOracleInfos, ...oracleInfos]
      .filter(({ oracle }) => oracle !== zeroAddress)
      .map(this.#getOracleInfo.bind(this))
      .filter(nonNullable)
  }

  /**
   * Get the oracle information from the store or decode it.
   * @private
   * @param {OracleDetailedInfo} params - The detailed oracle information.
   * @returns {OracleTypes} - The oracle information.
   */
  #getOracleInfo(params: OracleDetailedInfo): OracleTypes | undefined {
    const { name, oracleInfo, oracle, vault } = params
    const functionName = oracleNameToFunction[name]

    if (!functionName || oracleInfo === "0x") return undefined

    if (!this.oracleInfos.has(getAddress(vault))) {
      this.oracleInfos.set(getAddress(vault), params)
    }

    const abiItem = getAbiItem({
      abi: contractBook.oracleTypes.abi,
      name: functionName,
    })

    const abiParams = decodeAbiParameters(abiItem.outputs, oracleInfo)[0]

    return { ...abiParams, oracle, vault }
  }

  /**
   * Get the name list of base vaults on EulerRouter and Cross Adapter
   * @public
   * @param {OracleDetailedInfo} params - The detailed oracle information.
   * @returns {BaseOracleNamesAndQuotes} - The oracle information, names and base vault.
   */
  getBaseOracleNamesAndQuotes(
    params: OracleDetailedInfo,
  ): BaseOracleNamesAndQuotes {
    const names = new Set<string>()
    const oracles: Record<Address, OracleDecoded[]> = {}
    const baseVault: Address = zeroAddress

    // Early return if no valid function name
    const fn = oracleNameToFunction[params.name]
    if (!fn || params.oracleInfo === "0x") {
      return { names: [], oracles, baseVault }
    }

    try {
      const baseOracles = this.#decodeOracleInfo(fn, params.oracleInfo)

      if (isEulerRouterLazyCheck(baseOracles)) {
        return this.#handleEulerRouter(baseOracles, params, names, oracles)
      }

      if (isCrossAdapterLazyCheck(baseOracles)) {
        return this.#handleCrossAdapter(baseOracles, params, names, oracles)
      }

      if (isUniswapAdapterLazyCheck(baseOracles)) {
        return this.#handleUniswapAdapter(baseOracles, params, names, oracles)
      }

      const result = this.#handleStandardOracle(
        baseOracles,
        params,
        names,
        oracles,
      )

      if (result.names.length > 0) {
        return result
      }
      // Unknown oracle type warning
      console.warn(
        "[OracleBatch][getBaseOracleNamesAndQuotes] Bad configuration oracle type not recognized",
        params,
        baseOracles,
      )

      return { names: Array.from(names), oracles, baseVault }
    } catch (e) {
      console.error("[OracleBatch] Error in OracleBatch", e, params)
      return { names: [], oracles: {}, baseVault: zeroAddress }
    }
  }

  /**
   * Decodes oracle information from hex data
   * @private
   * @param {ContractFunctionName<typeof contractBook.oracleTypes.abi>} functionName - The function name to use for decoding
   * @param {Hex} oracleInfo - The oracle info hex data to decode
   * @returns {ContractFunctionResult<typeof contractBook.oracleTypes.abi>} - The decoded oracle information
   */
  #decodeOracleInfo(
    functionName: ContractFunctionName<typeof contractBook.oracleTypes.abi>,
    oracleInfo: Hex,
  ): ContractFunctionResult<typeof contractBook.oracleTypes.abi> {
    const abiItem = getAbiItem({
      abi: contractBook.oracleTypes.abi,
      name: functionName,
    })

    return decodeAbiParameters(abiItem.outputs, oracleInfo)[0]
  }

  /**
   * Handle standard (non-router) oracle types
   * @private
   * @param {ContractFunctionResult<typeof contractBook.oracleTypes.abi>} baseOracles - The decoded oracle info
   * @param {OracleDetailedInfo} params - The original oracle parameters
   * @param {Set<string>} names - Set to collect oracle names
   * @param {Record<Address, OracleDecoded[]>} oracles - Map to collect oracle data
   * @returns {BaseOracleNamesAndQuotes} - Processed oracle information
   */
  #handleStandardOracle(
    baseOracles: ContractFunctionResult<typeof contractBook.oracleTypes.abi>,
    params: OracleDetailedInfo,
    names: Set<string>,
    oracles: Record<Address, OracleDecoded[]>,
  ): BaseOracleNamesAndQuotes {
    names.add(params.name)
    const baseVault =
      "base" in baseOracles ? baseOracles.base || zeroAddress : zeroAddress

    if ("base" in baseOracles) {
      oracles[baseOracles.base] = [
        {
          base: baseOracles.base,
          quote: baseOracles.quote,
          oracle: params.oracle,
          name: params.name,
          assets: undefined,
        },
      ]
    }

    return {
      names: Array.from(names),
      oracles,
      baseVault,
    }
  }

  /**
   * Handle Uniswap adapter oracle type
   * @private
   * @param {OracleType<"getUniswapV3OracleInfo">} baseOracles - The decoded Uniswap oracle info
   * @param {OracleDetailedInfo} params - The original oracle parameters
   * @param {Set<string>} names - Set to collect oracle names
   * @param {Record<Address, OracleDecoded[]>} oracles - Map to collect oracle data
   * @returns {BaseOracleNamesAndQuotes} - Processed oracle information
   */
  #handleUniswapAdapter(
    baseOracles: OracleType<"getUniswapV3OracleInfo">,
    params: OracleDetailedInfo,
    names: Set<string>,
    oracles: Record<Address, OracleDecoded[]>,
  ): BaseOracleNamesAndQuotes {
    names.add(params.name)
    const baseVault = baseOracles.tokenA || zeroAddress

    // TokenA oracle
    oracles[baseOracles.tokenA] = [
      {
        base: baseOracles.tokenA,
        quote: baseOracles.tokenB,
        oracle: params.oracle,
        name: params.name,
        assets: undefined,
      },
    ]

    // TokenB oracle
    oracles[baseOracles.tokenB] = [
      {
        base: baseOracles.tokenB,
        quote: baseOracles.tokenA,
        oracle: params.oracle,
        name: params.name,
        assets: undefined,
      },
    ]

    return {
      names: Array.from(names),
      oracles,
      baseVault,
    }
  }

  /**
   * Handle Cross Adapter oracle type
   * @private
   * @param {OracleType<"getCrossAdapterInfo">} baseOracles - The decoded Cross Adapter oracle info
   * @param {OracleDetailedInfo} params - The original oracle parameters
   * @param {Set<string>} names - Set to collect oracle names
   * @param {Record<Address, OracleDecoded[]>} oracles - Map to collect oracle data
   * @returns {BaseOracleNamesAndQuotes} - Processed oracle information
   */
  #handleCrossAdapter(
    baseOracles: OracleType<"getCrossAdapterInfo">,
    _params: OracleDetailedInfo,
    names: Set<string>,
    oracles: Record<Address, OracleDecoded[]>,
  ): BaseOracleNamesAndQuotes {
    // Get oracle names for base and cross
    const nameBase = deepCheckIfIsRedstoneClasicOracle(
      baseOracles,
      baseOracles.oracleBaseCrossInfo.name,
      baseOracles.oracleBaseCrossInfo.oracleInfo,
    )

    const nameCross = deepCheckIfIsRedstoneClasicOracle(
      baseOracles,
      baseOracles.oracleCrossQuoteInfo.name,
      baseOracles.oracleCrossQuoteInfo.oracleInfo,
    )

    names.add(nameBase)
    names.add(nameCross)

    const baseVault = baseOracles.base || zeroAddress

    // Set up the oracles information
    oracles[baseOracles.base] = [
      {
        base: baseOracles.base,
        quote: baseOracles.cross,
        oracle: baseOracles.oracleBaseCrossInfo.oracle,
        name: nameBase,
        assets: undefined,
      },
      {
        base: baseOracles.cross,
        quote: baseOracles.quote,
        oracle: baseOracles.oracleCrossQuoteInfo.oracle,
        name: nameCross,
        assets: undefined,
      },
    ]

    return {
      names: Array.from(names),
      oracles,
      baseVault,
    }
  }

  /**
   * Handle Euler Router oracle type (most complex case)
   * @private
   * @param {OracleType<"getEulerRouterInfo">} baseOracles - The decoded Euler Router oracle info
   * @param {OracleDetailedInfo} params - The original oracle parameters
   * @param {Set<string>} names - Set to collect oracle names
   * @param {Record<Address, OracleDecoded[]>} oracles - Map to collect oracle data
   * @returns {BaseOracleNamesAndQuotes} - Processed oracle information
   */
  #handleEulerRouter(
    baseOracles: OracleType<"getEulerRouterInfo">,
    params: OracleDetailedInfo,
    names: Set<string>,
    oracles: Record<Address, OracleDecoded[]>,
  ): BaseOracleNamesAndQuotes {
    const baseVault = baseOracles.bases[0] || zeroAddress

    // Process each resolved oracle
    for (let i = 0; i < baseOracles.resolvedOraclesInfo.length; i++) {
      const baseInfo = baseOracles.resolvedOraclesInfo[i]
      const baseAsset = baseOracles.bases[i]
      const resolvedAsset = baseOracles.resolvedAssets[i]

      // Skip invalid entries
      if (!baseInfo || !baseAsset || baseInfo.oracle === zeroAddress) {
        continue
      }

      const functionName = oracleNameToFunction[baseInfo.name]

      if (functionName && baseInfo.oracleInfo !== "0x") {
        // Decode the internal oracle info
        const internalOracles = this.#decodeOracleInfo(
          functionName,
          baseInfo.oracleInfo,
        )

        if (isCrossAdapterLazyCheck(internalOracles)) {
          this.#processRouterCrossAdapter(
            internalOracles,
            { ...baseInfo, vault: baseAsset },
            baseAsset,
            resolvedAsset,
            names,
            oracles,
            i,
          )
        } else if (isUniswapAdapterLazyCheck(internalOracles)) {
          this.#processRouterUniswapAdapter(
            internalOracles,
            { ...baseInfo, vault: baseAsset },
            baseAsset,
            resolvedAsset,
            names,
            oracles,
            i,
          )
        } else if (!isEulerRouterLazyCheck(internalOracles)) {
          this.#processRouterStandardOracle(
            internalOracles,
            { ...baseInfo, vault: baseAsset },
            baseAsset,
            resolvedAsset,
            names,
            oracles,
            i,
          )
        } else if (isPendleOracleLazyCheck(internalOracles)) {
          this.#processRouterPendleAdapter(
            internalOracles,
            { ...baseInfo, vault: baseAsset },
            baseAsset,
            resolvedAsset,
            names,
            oracles,
            i,
          )
        } else {
          if (baseInfo?.name === "EulerRouter") {
            console.log(
              "[OracleBatch][#handleEulerRouter] Nested EulerRouter",
              params,
              baseInfo,
              internalOracles,
            )
            return this.#handleEulerRouter(
              internalOracles,
              params,
              names,
              oracles,
            )
          }
          // Nested EulerRouter - uncommon case
          console.warn(
            "[OracleBatch][#handleEulerRouter] Bad configuration oracle EulerRouter inside of EulerRouter",
            params,
            internalOracles,
          )
        }
      } else {
        names.add(baseInfo.name)
        oracles[baseAsset] = [
          {
            base:
              (Array.isArray(resolvedAsset) ? resolvedAsset[0] : baseAsset) ||
              NO_BASE_ADDRESS,
            quote:
              (Array.isArray(resolvedAsset) ? resolvedAsset[1] : baseAsset) ||
              NO_QUOTE_ADDRESS,
            oracle: baseInfo.oracle,
            name: baseInfo.name,
            assets: resolvedAsset,
          },
        ]
      }
    }

    return {
      names: Array.from(names),
      oracles,
      baseVault,
    }
  }

  /**
   * Process a standard oracle within an Euler Router
   * @private
   */
  #processRouterStandardOracle(
    internalOracles: ContractFunctionResult<
      typeof contractBook.oracleTypes.abi
    >,
    baseInfo: OracleDetailedInfo & { vault?: Address },
    baseAsset: Address,
    resolvedAsset: readonly Address[] | undefined,
    names: Set<string>,
    oracles: Record<Address, OracleDecoded[]>,
    index: number,
  ): void {
    // Get the actual oracle name, checking for Redstone Classic special case
    const name = deepCheckIfIsRedstoneClasicOracle(
      internalOracles,
      baseInfo.name,
      undefined,
    )

    names.add(name)

    // Create the base oracle entry
    let standardOracles = [
      {
        base: "base" in internalOracles ? internalOracles.base : zeroAddress,
        quote: "quote" in internalOracles ? internalOracles.quote : zeroAddress,
        oracle: baseInfo.oracle,
        name: name,
        assets: resolvedAsset,
      },
    ]

    // Handle ERC4626 vault case
    if (isERC4626Oracle(resolvedAsset, index) && Array.isArray(resolvedAsset)) {
      names.add("ERC4626Vault")

      // Add ERC4626 vault entry
      standardOracles = [
        {
          base: resolvedAsset[1] ? resolvedAsset[0] : baseAsset,
          quote:
            resolvedAsset[1] ||
            resolvedAsset[0] ||
            ("base" in internalOracles ? internalOracles.base : zeroAddress),
          oracle: resolvedAsset[1] ? resolvedAsset[0] : baseAsset,
          name: "ERC4626Vault",
          assets: resolvedAsset,
        },
        ...standardOracles,
      ]
    }

    oracles[baseAsset] = standardOracles
  }

  /**
   * Process a Uniswap adapter within an Euler Router
   * @private
   */
  #processRouterUniswapAdapter(
    internalOracles: OracleType<"getUniswapV3OracleInfo">,
    baseInfo: OracleDetailedInfo & { vault?: Address },
    baseAsset: Address,
    resolvedAsset: readonly Address[] | undefined,
    names: Set<string>,
    oracles: Record<Address, OracleDecoded[]>,
    index: number,
  ): void {
    names.add(baseInfo.name)

    // Create oracle entries for both tokens
    let tokenAOracles = [
      {
        base: internalOracles.tokenA,
        quote: internalOracles.tokenB,
        oracle: baseInfo.oracle,
        name: baseInfo.name,
        assets: resolvedAsset,
      },
    ]

    let tokenBOracles = [
      {
        base: internalOracles.tokenB,
        quote: internalOracles.tokenA,
        oracle: baseInfo.oracle,
        name: baseInfo.name,
        assets: resolvedAsset,
      },
    ]

    // Handle ERC4626 vault case
    if (isERC4626Oracle(resolvedAsset, index) && Array.isArray(resolvedAsset)) {
      names.add("ERC4626Vault")

      // Add ERC4626 vault entries for both tokens
      tokenAOracles = [
        {
          base: resolvedAsset[1] ? resolvedAsset[0] : baseAsset,
          quote: resolvedAsset[1] || resolvedAsset[0] || internalOracles.tokenA,
          oracle: resolvedAsset[1] ? resolvedAsset[0] : baseAsset,
          name: "ERC4626Vault",
          assets: resolvedAsset,
        },
        ...tokenAOracles,
      ]

      tokenBOracles = [
        {
          base: resolvedAsset[1] ? resolvedAsset[0] : baseAsset,
          quote: resolvedAsset[1] || resolvedAsset[0] || internalOracles.tokenB,
          oracle: resolvedAsset[1] ? resolvedAsset[0] : baseAsset,
          name: "ERC4626Vault",
          assets: resolvedAsset,
        },
        ...tokenBOracles,
      ]
    }

    oracles[internalOracles.tokenA] = tokenAOracles
    oracles[internalOracles.tokenB] = tokenBOracles
  }

  /**
   * Process a Cross Adapter within an Euler Router
   * @private
   */
  #processRouterCrossAdapter(
    internalOracles: OracleType<"getCrossAdapterInfo">,
    baseInfo: OracleDetailedInfo & { vault?: Address },
    baseAsset: Address,
    resolvedAsset: readonly Address[] | undefined,
    names: Set<string>,
    oracles: Record<Address, OracleDecoded[]>,
    index: number,
  ): void {
    const crossOracles: OracleDecoded[] = []

    // Process base cross info
    this.#processBaseCrossInfo(
      internalOracles,
      {
        ...baseInfo,
        vault: baseAsset,
      },
      resolvedAsset,
      crossOracles,
      names,
    )

    // Process cross quote info
    this.#processCrossQuoteInfo(
      internalOracles,
      {
        ...baseInfo,
        vault: baseAsset,
      },
      resolvedAsset,
      crossOracles,
      names,
    )

    // Handle ERC4626 vault case
    if (isERC4626Oracle(resolvedAsset, index) && Array.isArray(resolvedAsset)) {
      names.add("ERC4626Vault")

      crossOracles.push({
        base: resolvedAsset[1] ? resolvedAsset[0] : baseAsset,
        quote: resolvedAsset[1] || resolvedAsset[0] || internalOracles.base,
        oracle: resolvedAsset[1] ? resolvedAsset[0] : baseAsset,
        name: "ERC4626Vault",
        assets: resolvedAsset,
      })
    }

    oracles[baseAsset] = crossOracles
  }

  /**
   * Process the base cross info part of a Cross Adapter
   * @private
   */
  #processBaseCrossInfo(
    internalOracles: OracleType<"getCrossAdapterInfo">,
    baseInfo: OracleDetailedInfo & { vault: Address },
    resolvedAsset: readonly Address[] | undefined,
    crossOracles: OracleDecoded[],
    names: Set<string>,
  ): void {
    const nameBase = deepCheckIfIsRedstoneClasicOracle(
      internalOracles,
      internalOracles.oracleBaseCrossInfo.name,
      internalOracles.oracleBaseCrossInfo.oracleInfo,
    )

    // Handle nested Cross Adapter case
    if (nameBase === "CrossAdapter") {
      this.#processNestedCrossAdapter(
        "base",
        internalOracles,
        baseInfo,
        resolvedAsset,
        crossOracles,
        names,
      )
    } else {
      // Standard case
      names.add(nameBase)
      crossOracles.push({
        base: internalOracles.base,
        quote: internalOracles.cross,
        oracle: internalOracles.oracleBaseCrossInfo.oracle,
        name: nameBase,
        assets: resolvedAsset,
      })
    }
  }

  /**
   * Process the cross quote info part of a Cross Adapter
   * @private
   */
  #processCrossQuoteInfo(
    internalOracles: OracleType<"getCrossAdapterInfo">,
    baseInfo: OracleDetailedInfo & { vault: Address },
    resolvedAsset: readonly Address[] | undefined,
    crossOracles: OracleDecoded[],
    names: Set<string>,
  ): void {
    const nameCross = deepCheckIfIsRedstoneClasicOracle(
      internalOracles,
      internalOracles.oracleCrossQuoteInfo.name,
      internalOracles.oracleCrossQuoteInfo.oracleInfo,
    )

    // Handle nested Cross Adapter case
    if (nameCross === "CrossAdapter") {
      this.#processNestedCrossAdapter(
        "quote",
        internalOracles,
        baseInfo,
        resolvedAsset,
        crossOracles,
        names,
      )
    } else {
      // Standard case
      names.add(nameCross)
      crossOracles.push({
        base: internalOracles.cross,
        quote: internalOracles.quote,
        oracle: internalOracles.oracleCrossQuoteInfo.oracle,
        name: nameCross,
        assets: resolvedAsset,
      })
    }
  }

  /**
   * Process a nested Cross Adapter (complex case)
   * @private
   */
  #processNestedCrossAdapter(
    part: "base" | "quote",
    internalOracles: OracleType<"getCrossAdapterInfo">,
    baseInfo: OracleDetailedInfo & { vault: Address },
    resolvedAsset: readonly Address[] | undefined,
    crossOracles: OracleDecoded[],
    names: Set<string>,
  ): void {
    const functionName = oracleNameToFunction[baseInfo.name]
    if (!functionName) return

    const oracleInfoKey =
      part === "base"
        ? internalOracles.oracleBaseCrossInfo.oracleInfo
        : internalOracles.oracleCrossQuoteInfo.oracleInfo

    if (oracleInfoKey === "0x") return

    // Decode the nested cross adapter
    const nestedOracles = this.#decodeOracleInfo(functionName, oracleInfoKey)

    if (!isCrossAdapterLazyCheck(nestedOracles)) return

    // Process base part of nested adapter
    const nameBase = deepCheckIfIsRedstoneClasicOracle(
      nestedOracles,
      nestedOracles.oracleBaseCrossInfo.name,
      nestedOracles.oracleBaseCrossInfo.oracleInfo,
    )

    names.add(nameBase)
    crossOracles.push({
      base: nestedOracles.base,
      quote: nestedOracles.cross,
      oracle: nestedOracles.oracleBaseCrossInfo.oracle,
      name: nameBase,
      assets: resolvedAsset,
    })

    // Process quote part of nested adapter
    const nameCross = deepCheckIfIsRedstoneClasicOracle(
      nestedOracles,
      nestedOracles.oracleCrossQuoteInfo.name,
      nestedOracles.oracleCrossQuoteInfo.oracleInfo,
    )

    names.add(nameCross)
    crossOracles.push({
      base: nestedOracles.cross,
      quote: nestedOracles.quote,
      oracle: nestedOracles.oracleCrossQuoteInfo.oracle,
      name: nameCross,
      assets: resolvedAsset,
    })
  }

  #processRouterPendleAdapter(
    internalOracles: OracleType<"getPendleOracleInfo">,
    _baseInfo: OracleDetailedInfo & { vault?: Address },
    baseAsset: Address,
    resolvedAsset: readonly Address[] | undefined,
    names: Set<string>,
    oracles: Record<Address, OracleDecoded[]>,
    _index: number,
  ): void {
    names.add("PendleUniversalOracle")
    if (!resolvedAsset) {
      console.warn("[OracleBatch] Pendle adapter not supported")
      return
    }
    oracles[baseAsset] = [
      {
        base: resolvedAsset[1] ? resolvedAsset[0] || zeroAddress : baseAsset,
        quote:
          resolvedAsset[1] ||
          resolvedAsset[0] ||
          ("base" in internalOracles ? internalOracles.base : zeroAddress),
        oracle: resolvedAsset[1] ? resolvedAsset[0] || zeroAddress : baseAsset,
        name: "PendleUniversalOracle",
        assets: resolvedAsset,
      },
    ]
  }
}
